import React, { useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { Link } from "react-router-dom";
import Select from "react-select";
import moment from "moment";
import {
  LuBuilding2,
  LuCalendar,
  LuCreditCard,
  LuDatabase,
  LuExternalLink,
  LuFileText,
  LuFilter,
  LuListFilter,
  LuPencil,
  LuPlus,
  LuSearch,
  LuTrash2,
  LuUser,
} from "react-icons/lu";
import { toast } from "react-toastify";

import AccountFormModal from "./AccountFormModal";
import Pagination from "../pagination";
import Button from "../ui/Button";
import Input from "../ui/Input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/Table";
import {
  EmptyState,
  LoadingState,
  TableEmptyRow,
  TableLoadingRow,
} from "../ui/TableStateRow";
import cx from "../ui/cx";
import {
  actionBarClass,
  buttonSpinnerClass,
  cardClass,
  cardContentClass,
  cardContentFlushClass,
  cardHeaderClass,
  cardTitleClass,
  creditCellClass,
  creditTextClass,
  debitCellClass,
  debitTextClass,
  fieldIconClass,
  fieldInputClass,
  filterFooterClass,
  filterGridClass,
  filterLabelClass,
  mobileCardClass,
  moneyCellClass,
  segmentActiveClass,
  segmentButtonClass,
  segmentInactiveClass,
  segmentTrackClass,
  summaryCardTitleClass,
  summaryCardValueClass,
  tableHeadCellClass,
} from "../ui/styles";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  fetchAccountsListData,
  fetchAccountTypesList,
  fetchPartiesList,
  getAccounts,
  getAccountTypes,
  getEditAccount,
  getIsAccountModalOpen,
  getIsShowAccounts,
  getParties,
  getTotalAccounts,
  getTotalCredit,
  getTotalDebit,
  setEditAccount,
  setIsShowAccounts,
  toggleAccountModal,
} from "../../features/accounts/accountsSlice";
import {
  getListState,
  saveListState,
} from "../../features/listState/listStateSlice";
import { getLoggingUser } from "../../features/loggingUser/loggingUserSlice";
import { updatePdfFile } from "../../features/settings/settingsSlice";
import useConfirmPopup from "../../context/useConfirmPopup";
import { DELETE, GET, POST, PUT } from "../../services/axios.service";
import { hasPermission, permission } from "../../static/permission";
import { route } from "../../static/route";
import { iconSelectStyles, toastStyles } from "../../static/styles";
import { AccountsProps, IFormInput, OptionsProps } from "../../types/accounts";

const formatAmount = (value: number | null) =>
  (value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const formatDate = (value: string) => moment(value).format("DD-MM-YYYY");

const formatDateTime = (value: string) =>
  moment(value).format("DD-MM-YYYY hh:mm A");

const actionSpinnerClass = cx(
  buttonSpinnerClass,
  "mr-0 border-[#113F67]/30 border-t-[#113F67]"
);

const cardRowClass = "flex items-start justify-between gap-4";

const cardLabelClass = cx(tableHeadCellClass, "shrink-0");

const sourceLinkClass =
  "group inline-flex items-center gap-2 text-teal-600 hover:text-teal-800 transition-colors";

const sourceTextClass = "inline-flex items-center gap-2 text-gray-600";

const noteLineClass = "flex items-start gap-1.5 text-sm text-gray-600";

const pathOf = (path: string, id: number) =>
  path.replace(":id", id as unknown as string);

// Where an entry booked by another module came from. `permission` opens `path`.
const sourceOf = (account: AccountsProps) => {
  if (account.payment_id) {
    return {
      label: "પાર્ટી પેમેન્ટ",
      id: account.payment_id,
      name: account.payment?.party?.firm_name,
      targetId: account.payment?.party_id,
      path: route.payments,
      permission: permission.paymentsRead,
    };
  }

  if (account.salary_id) {
    return {
      label: "પગાર",
      id: account.salary_id,
      name: account.salary?.employee?.name,
      targetId: account.salary?.employee_id,
      path: route.salaries,
      permission: permission.salariesRead,
    };
  }

  return null;
};

const typeHead = (account: AccountsProps) => {
  const source = sourceOf(account);

  if (source && account.salary_id) {
    return (
      <div className="flex items-center gap-1.5">
        <LuUser className="h-3.5 w-3.5 text-blue-600" />
        <span className="text-sm font-semibold text-blue-900">
          {source.label}
        </span>
      </div>
    );
  }

  if (source) {
    return (
      <div className="flex items-center gap-1.5">
        <LuCreditCard className="h-3.5 w-3.5 text-green-600" />
        <span className="text-sm font-semibold text-green-900">
          {source.label}
        </span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1.5">
      <LuBuilding2 className="h-3.5 w-3.5 text-blue-600" />
      <span className="text-sm font-semibold text-blue-900">
        {account.account_type?.name || "-"}
      </span>
    </div>
  );
};

// The source links to its page only when the user may open it. Entries from a hidden module (salaries) show the same
// details without a link.
const typeDetails = (account: AccountsProps, permissions?: string[]) => {
  const source = sourceOf(account);

  if (
    source &&
    source.targetId &&
    hasPermission(permissions, source.permission)
  ) {
    return (
      <Link
        to={pathOf(source.path, source.targetId)}
        target="_blank"
        rel="noopener noreferrer"
        className={sourceLinkClass}
      >
        <span className="text-sm font-medium">ID: {source.id}</span>
        {source.name && (
          <>
            <span className="text-gray-400">•</span>
            <span className="text-sm">{source.name}</span>
          </>
        )}
        <LuExternalLink className="h-3.5 w-3.5" />
      </Link>
    );
  }

  if (source) {
    return (
      <span className={sourceTextClass}>
        <span className="text-sm font-medium">ID: {source.id}</span>
        {source.name && (
          <>
            <span className="text-gray-400">•</span>
            <span className="text-sm">{source.name}</span>
          </>
        )}
      </span>
    );
  }

  if (account.account_type?.phone) {
    return (
      <div className="text-sm text-gray-600">{account.account_type.phone}</div>
    );
  }

  return null;
};

const listKey = "accounts";

const AccountList = () => {
  const dispatch = useAppDispatch();
  const confirmDelete = useConfirmPopup();

  const loggingUser = useAppSelector(getLoggingUser);
  const savedList = useAppSelector((state) => getListState(state, listKey));

  const canCreate = hasPermission(
    loggingUser?.permissions,
    permission.accountsCreate
  );
  const canUpdate = hasPermission(
    loggingUser?.permissions,
    permission.accountsUpdate
  );
  const canDelete = hasPermission(
    loggingUser?.permissions,
    permission.accountsDelete
  );
  const canReport = hasPermission(
    loggingUser?.permissions,
    permission.accountsReport
  );
  const canReadAccountTypes = hasPermission(
    loggingUser?.permissions,
    permission.accountTypesRead
  );
  // The party filter is offered only to a user who can open the parties module.
  const canReadParties = hasPermission(
    loggingUser?.permissions,
    permission.partiesRead
  );

  const actionCount = (canUpdate ? 1 : 0) + (canDelete ? 1 : 0);
  const tableColSpan = 8 + (actionCount > 0 ? 1 : 0);
  const toolbarCount = 1 + (canReport ? 1 : 0) + (canCreate ? 2 : 0);

  const isShowAccounts = useAppSelector(getIsShowAccounts);
  const accounts = useAppSelector(getAccounts);
  const totalAccounts = useAppSelector(getTotalAccounts);
  const editAccount = useAppSelector(getEditAccount);
  const isAccountModalOpen = useAppSelector(getIsAccountModalOpen);
  const parties = useAppSelector(getParties);
  const accountTypes = useAppSelector(getAccountTypes);
  const totalCredit = useAppSelector(getTotalCredit);
  const totalDebit = useAppSelector(getTotalDebit);

  const [partyOption, setPartyOption] = useState<OptionsProps[]>([]);
  const [accountTypeOption, setAccountTypeOption] = useState<OptionsProps[]>(
    []
  );
  const [statusOption] = useState<OptionsProps[]>([
    {
      value: 1,
      label: "આવક",
    },
    {
      value: 2,
      label: "જાવક",
    },
  ]);

  interface FilterFormInput {
    name: string;
    fromDate: string;
    toDate: string;
    party_id: number | null;
    account_type_id: number | null;
    status: number | null;
  }

  interface ReportFormInput {
    fromDate: string;
    toDate: string;
  }

  const emptyFilters: FilterFormInput = {
    name: "",
    fromDate: "",
    toDate: "",
    party_id: null,
    account_type_id: null,
    status: null,
  };

  const emptyReportFilters: ReportFormInput = {
    fromDate: "",
    toDate: "",
  };

  const { handleSubmit, register, reset, getValues, control, setValue } =
    useForm<FilterFormInput>({ defaultValues: savedList.filters });

  const {
    handleSubmit: handleSubmitReport,
    register: registerReport,
    reset: resetReport,
    getValues: getValuesReport,
  } = useForm<ReportFormInput>({ defaultValues: savedList.reportFilters });

  const [page, setPage] = useState<number>(savedList.page || 1);
  const [pageSize, setPageSize] = useState<number>(savedList.pageSize || 50);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isReportLoading, setIsReportLoading] = useState<boolean>(false);
  const [isActionLoading, setIsActionLoading] = useState<number>(0);
  const [showFilter, setShowFilter] = useState<boolean>(
    !!savedList.showFilter
  );
  const [showExport, setShowExport] = useState<boolean>(
    !!savedList.showExport
  );

  const fetchId = useRef(0);

  const summaryCards = [
    { label: "ટોટલ આવક", value: totalCredit },
    { label: "ટોટલ જાવક", value: totalDebit },
    { label: "બેલેન્સ", value: totalCredit - totalDebit },
  ];

  const paginate = (pageNumber: number) => {
    setPage(pageNumber);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setPage(1);
  };

  const handleShowAccountForm = async (type: number) => {
    await dispatch(setEditAccount(null));
    dispatch(toggleAccountModal(type));
  };

  const handleEditAccount = async (account: AccountsProps) => {
    await dispatch(setEditAccount(account));
    dispatch(toggleAccountModal(account.status === 0 ? 1 : 2));
  };

  const handleSaveAccount = async (data: IFormInput) => {
    try {
      if (editAccount && editAccount.id) {
        await PUT(`/v1/admin/accounts/${editAccount.id}`, {
          date: data.date,
          amount: data.amount,
          account_type_id: data.account_type_id,
          status: data.status,
          description: data.description,
        })
          .then(async (response: any) => {
            if (response.data.status === "success") {
              await dispatch(setEditAccount(null));

              if (page === 1) {
                fetchAccountsList(page, pageSize);
              } else {
                setPage(1);
              }

              toast.success(response.data.message, {
                ...(toastStyles as any),
              });
            } else {
              toast.error(response.data.message, {
                ...(toastStyles as any),
              });
            }
          })
          .catch((error: any) => {
            toast.error(error.data.message, {
              ...(toastStyles as any),
            });
          });
      } else {
        await POST("/v1/admin/accounts", {
          date: data.date,
          amount: data.amount,
          account_type_id: data.account_type_id,
          status: data.status,
          description: data.description,
        })
          .then(async (response: any) => {
            if (response.data.status === "success") {
              if (page === 1) {
                fetchAccountsList(page, pageSize);
              } else {
                setPage(1);
              }

              toast.success(response.data.message, {
                ...(toastStyles as any),
              });
            } else {
              toast.error(response.data.message, {
                ...(toastStyles as any),
              });
            }
          })
          .catch((error: any) => {
            toast.error(error.data.message, {
              ...(toastStyles as any),
            });
          });
      }
    } catch (error) {
      toast.error("કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.", {
        ...(toastStyles as any),
      });
    }
  };

  const handleDeleteAccount = async (id: number) => {
    try {
      await DELETE(`/v1/admin/accounts/${id}`)
        .then(async (response: any) => {
          if (response.data.status === "success") {
            const pageNumber = accounts.length > 1 ? page : page - 1;
            if (page === pageNumber || pageNumber < 1) {
              fetchAccountsList(page, pageSize);
            } else {
              setPage(pageNumber);
            }

            toast.success(response.data.message, {
              ...(toastStyles as any),
            });
          } else {
            toast.error(response.data.message, {
              ...(toastStyles as any),
            });
          }
        })
        .catch((error: any) => {
          toast.error(error.data.message, {
            ...(toastStyles as any),
          });
        });
    } catch (error) {
      toast.error("કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.", {
        ...(toastStyles as any),
      });
    }
  };

  const handleDeleteRow = async (id: number) => {
    const confirmed = await confirmDelete(
      `તમે આને પાછું ફેરવી શકશો નહીં!`,
      `શું તમે ખાતરી આપો છો?`,
      "હા, તેને કાઢી નાખો!",
      true
    );

    if (confirmed) {
      setIsActionLoading(id);
      await handleDeleteAccount(id);
      setIsActionLoading(0);
    }
  };

  const handleOnFilterSubmit = async () => {
    if (page === 1) {
      fetchAccountsList(page, pageSize);
    } else {
      setPage(1);
    }
  };

  const handleClearFilter = () => {
    reset(emptyFilters);
    if (page === 1) {
      fetchAccountsList(page, pageSize);
    } else {
      setPage(1);
    }
  };

  const handleClearReportFilter = () => {
    resetReport(emptyReportFilters);
    dispatch(
      saveListState({
        key: listKey,
        value: { reportFilters: emptyReportFilters },
      })
    );
  };

  const handleExportReport = async () => {
    try {
      setIsReportLoading(true);
      const data = getValuesReport();

      dispatch(
        saveListState({ key: listKey, value: { reportFilters: data } })
      );

      await GET(`/v1/admin/get-accounts-report`, {
        fromDate: data?.fromDate || null,
        toDate: data?.toDate || null,
      })
        .then(async (response: any) => {
          if (response.data.status === "success") {
            dispatch(updatePdfFile(response.data.data));
          } else {
            toast.error(response.data.message, {
              ...(toastStyles as any),
            });
          }
        })
        .catch((error: any) => {
          toast.error(error.data.message, {
            ...(toastStyles as any),
          });
        });
    } catch (error) {
      toast.error("કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.", {
        ...(toastStyles as any),
      });
    } finally {
      setIsReportLoading(false);
    }
  };

  const fetchAccountsList = async (page: number, size: number) => {
    const data = getValues();
    const currentFetch = ++fetchId.current;

    dispatch(saveListState({ key: listKey, value: { filters: data } }));
    setIsLoading(true);

    dispatch(
      fetchAccountsListData({
        page: page,
        size: size,
        search: data?.name || null,
        fromDate: data?.fromDate || null,
        toDate: data?.toDate || null,
        party_id: data?.party_id || null,
        account_type_id: data?.account_type_id || null,
        status: data?.status || null,
      })
    ).then((action: any) => {
      const result = action?.payload?.data;

      if (currentFetch === fetchId.current && result?.status !== "success") {
        toast.error(
          result?.message || "કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.",
          { ...(toastStyles as any) }
        );
      }

      // A saved page that is now past the end (its rows deleted, say) moves to the last page.
      const lastPage = Math.max(
        1,
        Math.ceil((Number(result?.data?.total_records) || 0) / size)
      );

      if (
        currentFetch === fetchId.current &&
        page > 1 &&
        result?.status === "success" &&
        !result?.data?.records?.length &&
        lastPage < page
      ) {
        setPage(lastPage);
        return;
      }

      setIsLoading(false);
    });
  };

  useEffect(() => {
    const options =
      parties?.map((party) => ({
        value: party.id,
        label: party.phone
          ? `${party.firm_name} (${party.phone})`
          : party.firm_name,
      })) ?? [];
    setPartyOption(options);
  }, [parties]);

  useEffect(() => {
    const options =
      accountTypes?.map((accountType) => ({
        value: accountType.id,
        label: accountType.name,
      })) ?? [];
    setAccountTypeOption(options);
  }, [accountTypes]);

  // Coming back to the list (from a record's page, say) shows the same page and filters.
  useEffect(() => {
    dispatch(
      saveListState({
        key: listKey,
        value: {
          page,
          pageSize,
          showFilter,
          showExport,
        },
      })
    );
  }, [dispatch, page, pageSize, showFilter, showExport]);

  useEffect(() => {
    fetchAccountsList(page, pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize]);

  useEffect(() => {
    dispatch(fetchAccountTypesList());

    if (canReadParties) dispatch(fetchPartiesList());
  }, [dispatch, canReadParties]);

  return (
    <div className="space-y-4 md:space-y-6">
      <div className={actionBarClass}>
        <div className="flex items-center gap-3">
          <div className={segmentTrackClass}>
            <button
              type="button"
              onClick={() => isShowAccounts || dispatch(setIsShowAccounts(true))}
              className={cx(
                segmentButtonClass,
                isShowAccounts ? segmentActiveClass : segmentInactiveClass
              )}
            >
              રોજમેળ
            </button>

            {canReadAccountTypes && (
              <button
                type="button"
                onClick={() =>
                  !isShowAccounts || dispatch(setIsShowAccounts(false))
                }
                className={cx(
                  segmentButtonClass,
                  !isShowAccounts ? segmentActiveClass : segmentInactiveClass
                )}
              >
                પાર્ટીઓ
              </button>
            )}
          </div>
        </div>

        <div
          className={cx(
            "grid w-full gap-2 sm:flex sm:w-auto",
            toolbarCount === 1 ? "grid-cols-1" : "grid-cols-2"
          )}
        >
          <Button variant="outline" onClick={() => setShowFilter(!showFilter)}>
            <LuFilter className="h-4 w-4" />
            {showFilter ? "ફિલ્ટર છુપાવો" : "ફિલ્ટર બતાવો"}
          </Button>

          {canReport && (
            <Button variant="outline" onClick={() => setShowExport(!showExport)}>
              <LuFileText className="h-4 w-4" />
              રોજમેળ રિપોર્ટ
            </Button>
          )}

          {canCreate && (
            <Button variant="primary" onClick={() => handleShowAccountForm(1)}>
              <LuPlus className="h-4 w-4" />
              આવક
            </Button>
          )}

          {canCreate && (
            <Button variant="primary" onClick={() => handleShowAccountForm(2)}>
              <LuPlus className="h-4 w-4" />
              જાવક
            </Button>
          )}
        </div>
      </div>

      {showFilter && (
        <div className={cardClass}>
          <div className={cardHeaderClass}>
            <div className={cx(cardTitleClass, "text-lg")}>ફિલ્ટર</div>
          </div>

          <div className={cardContentClass}>
            <form onSubmit={handleSubmit(handleOnFilterSubmit)}>
              <div className={filterGridClass}>
                <div className="space-y-2">
                  <label className={filterLabelClass}>તારીખ થી</label>
                  <div className="relative">
                    <LuDatabase className={fieldIconClass} />
                    <Input
                      type="date"
                      className={fieldInputClass()}
                      {...register("fromDate")}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className={filterLabelClass}>તારીખ સુધી</label>
                  <div className="relative">
                    <LuDatabase className={fieldIconClass} />
                    <Input
                      type="date"
                      className={fieldInputClass()}
                      {...register("toDate")}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className={filterLabelClass}>શોધો</label>
                  <div className="relative">
                    <LuSearch className={fieldIconClass} />
                    <Input
                      placeholder="નોંધ દ્વારા શોધો"
                      className={fieldInputClass()}
                      {...register("name")}
                    />
                  </div>
                </div>

                {canReadParties && (
                  <div className="space-y-2">
                    <label className={filterLabelClass}>પાર્ટી</label>
                    <div className="relative">
                      <LuBuilding2 className={fieldIconClass} />
                      <Controller
                        {...register("party_id")}
                        control={control}
                        render={({ field }) => (
                          <Select
                            isClearable
                            {...field}
                            value={
                              partyOption.find(
                                (val) => val.value === field.value
                              ) || null
                            }
                            options={partyOption}
                            placeholder="પાર્ટી નું નામ સિલેક્ટ કરો"
                            styles={iconSelectStyles}
                            className="react-select-container"
                            classNamePrefix="react-select"
                            onChange={(val: any) =>
                              setValue("party_id", val?.value ?? null)
                            }
                          />
                        )}
                      />
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <label className={filterLabelClass}>એકાઉન્ટ ટાઈપ</label>
                  <div className="relative">
                    <LuListFilter className={fieldIconClass} />
                    <Controller
                      {...register("account_type_id")}
                      control={control}
                      render={({ field }) => (
                        <Select
                          isClearable
                          {...field}
                          value={
                            accountTypeOption.find(
                              (val) => val.value === field.value
                            ) || null
                          }
                          options={accountTypeOption}
                          placeholder="એકાઉન્ટ ટાઈપ સિલેક્ટ કરો"
                          styles={iconSelectStyles}
                          className="react-select-container"
                          classNamePrefix="react-select"
                          onChange={(val: any) =>
                            setValue("account_type_id", val?.value ?? null)
                          }
                        />
                      )}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className={filterLabelClass}>સ્ટેટ્સ</label>
                  <div className="relative">
                    <LuListFilter className={fieldIconClass} />
                    <Controller
                      {...register("status")}
                      control={control}
                      render={({ field }) => (
                        <Select
                          isClearable
                          {...field}
                          value={
                            statusOption.find(
                              (val) => val.value === field.value
                            ) || null
                          }
                          options={statusOption}
                          placeholder="સ્ટેટ્સ નું નામ સિલેક્ટ કરો"
                          styles={iconSelectStyles}
                          className="react-select-container"
                          classNamePrefix="react-select"
                          onChange={(val: any) =>
                            setValue("status", val?.value ?? null)
                          }
                        />
                      )}
                    />
                  </div>
                </div>
              </div>

              <div className={filterFooterClass}>
                <Button
                  type="button"
                  variant="outline"
                  className="max-sm:flex-1"
                  onClick={handleClearFilter}
                >
                  ફિલ્ટર સાફ કરો
                </Button>

                <Button
                  type="submit"
                  variant="primary"
                  className="max-sm:flex-1"
                >
                  ફિલ્ટર લાગુ કરો
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showExport && (
        <div className={cardClass}>
          <div className={cardHeaderClass}>
            <div className={cx(cardTitleClass, "text-lg")}>રોજમેળ રિપોર્ટ</div>
          </div>

          <div className={cardContentClass}>
            <form onSubmit={handleSubmitReport(handleExportReport)}>
              <div className={filterGridClass}>
                <div className="space-y-2">
                  <label className={filterLabelClass}>તારીખ થી</label>
                  <div className="relative">
                    <LuDatabase className={fieldIconClass} />
                    <Input
                      type="date"
                      className={fieldInputClass()}
                      {...registerReport("fromDate")}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className={filterLabelClass}>તારીખ સુધી</label>
                  <div className="relative">
                    <LuDatabase className={fieldIconClass} />
                    <Input
                      type="date"
                      className={fieldInputClass()}
                      {...registerReport("toDate")}
                    />
                  </div>
                </div>
              </div>

              <div className={filterFooterClass}>
                <Button
                  type="button"
                  variant="outline"
                  className="max-sm:flex-1"
                  onClick={handleClearReportFilter}
                >
                  ફિલ્ટર સાફ કરો
                </Button>

                <Button
                  type="submit"
                  variant="primary"
                  className="max-sm:flex-1"
                  disabled={isReportLoading}
                >
                  {isReportLoading && <span className={buttonSpinnerClass} />}
                  રિપોર્ટ બનાવો
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {summaryCards.map((card) => (
          <div key={card.label} className={cardClass}>
            <div className={cx(cardHeaderClass, "pb-2")}>
              <div className={summaryCardTitleClass}>{card.label}</div>
            </div>

            <div className={cardContentClass}>
              <div className={summaryCardValueClass}>
                {formatAmount(card.value)}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className={cx(cardClass, "max-sm:hidden")}>
        <div className={cardContentFlushClass}>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className={cx(tableHeadCellClass, "text-center")}>
                    ક્રમ
                  </TableHead>
                  <TableHead className={cx(tableHeadCellClass, "text-center")}>
                    એન્ટ્રી ID
                  </TableHead>
                  <TableHead className={cx(tableHeadCellClass, "text-center")}>
                    પ્રકાર
                  </TableHead>
                  <TableHead className={tableHeadCellClass}>વિગતો</TableHead>
                  <TableHead className={cx(tableHeadCellClass, "text-right")}>
                    આવક
                  </TableHead>
                  <TableHead className={cx(tableHeadCellClass, "text-right")}>
                    જાવક
                  </TableHead>
                  <TableHead className={cx(tableHeadCellClass, "text-right")}>
                    બેલેન્સ
                  </TableHead>
                  <TableHead className={tableHeadCellClass}>તારીખ</TableHead>
                  {actionCount > 0 && (
                    <TableHead className={cx(tableHeadCellClass, "text-right")}>
                      એકશન્સ બટન
                    </TableHead>
                  )}
                </TableRow>
              </TableHeader>

              {isLoading ? (
                <TableLoadingRow colSpan={tableColSpan} />
              ) : accounts.length === 0 ? (
                <TableEmptyRow
                  colSpan={tableColSpan}
                  label="રોજમેળ ની વિગતો મળી નથી"
                />
              ) : (
                <TableBody>
                  {accounts.map((account, index) => (
                    <TableRow key={account.id}>
                      <TableCell className="font-medium text-center">
                        {(page - 1) * pageSize + index + 1}
                      </TableCell>

                      <TableCell className="font-medium text-center">
                        {account.id}
                      </TableCell>

                      <TableCell className="text-center">
                        {account.status === 0 ? (
                          <p className={creditTextClass}>આવક</p>
                        ) : (
                          <p className={debitTextClass}>જાવક</p>
                        )}
                      </TableCell>

                      <TableCell>
                        <div className="min-w-0 space-y-1.5">
                          {typeHead(account)}
                          {typeDetails(account, loggingUser?.permissions)}
                          {account.description && (
                            <div className={noteLineClass}>
                              <LuFileText className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                              <span className="break-words">
                                {account.description}
                              </span>
                            </div>
                          )}
                        </div>
                      </TableCell>

                      <TableCell className={creditCellClass}>
                        {formatAmount(account.credit_amount)}
                      </TableCell>

                      <TableCell className={debitCellClass}>
                        {formatAmount(account.debit_amount)}
                      </TableCell>

                      <TableCell className={moneyCellClass}>
                        {formatAmount(account.balance)}
                      </TableCell>

                      <TableCell className="text-sm text-gray-500">
                        <div className="flex flex-col gap-1">
                          <span className="flex items-center gap-1.5">
                            <LuCalendar className="h-3.5 w-3.5 text-blue-800" />
                            {formatDate(account.date || account.created_at)}
                          </span>
                          <span className="flex items-center gap-1.5 text-xs text-gray-400">
                            <LuDatabase className="h-3.5 w-3.5 text-gray-400" />
                            {formatDateTime(account.created_at)}
                          </span>
                        </div>
                      </TableCell>

                      {actionCount > 0 && (
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            {isActionLoading === account.id ? (
                              <div className={actionSpinnerClass}></div>
                            ) : (
                              account.isEditable && (
                                <>
                                  {canUpdate && (
                                    <Button
                                      variant="ghost"
                                      size="iconSm"
                                      title="સંપાદિત કરો"
                                      onClick={() => handleEditAccount(account)}
                                    >
                                      <LuPencil className="h-4 w-4" />
                                    </Button>
                                  )}

                                  {canDelete && (
                                    <Button
                                      variant="ghost"
                                      size="iconSm"
                                      title="કાઢી નાખો"
                                      className="text-red-600 hover:text-red-700"
                                      onClick={() => handleDeleteRow(account.id)}
                                    >
                                      <LuTrash2 className="h-4 w-4" />
                                    </Button>
                                  )}
                                </>
                              )
                            )}
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              )}
            </Table>
          </div>
        </div>
      </div>

      <div className="space-y-4 sm:hidden">
        {isLoading ? (
          <div className={cx(mobileCardClass, "p-4 text-sm")}>
            <LoadingState />
          </div>
        ) : accounts.length === 0 ? (
          <div className={cx(mobileCardClass, "p-4 text-sm")}>
            <EmptyState label="રોજમેળ ની વિગતો મળી નથી" />
          </div>
        ) : (
          accounts.map((account, index) => (
            <div key={account.id} className={mobileCardClass}>
              <div className="flex items-start justify-between gap-3 border-b p-4">
                {typeHead(account)}
                <span className="shrink-0 text-sm text-gray-500">
                  ID: {account.id}
                </span>
              </div>

              <div className="space-y-3 p-4 text-sm">
                <div className={cardRowClass}>
                  <span className={cardLabelClass}>ક્રમ</span>
                  <span className="font-semibold text-primary">
                    {(page - 1) * pageSize + index + 1}
                  </span>
                </div>

                {(typeDetails(account, loggingUser?.permissions) ||
                  account.description) && (
                  <div className={cardRowClass}>
                    <span className={cardLabelClass}>વિગતો</span>
                    <span className="min-w-0 space-y-1.5 text-right">
                      {typeDetails(account, loggingUser?.permissions)}
                      {account.description && (
                        <span className="block break-words text-gray-600">
                          {account.description}
                        </span>
                      )}
                    </span>
                  </div>
                )}

                <div className={cardRowClass}>
                  <span className={cardLabelClass}>આવક</span>
                  <span className={creditCellClass}>
                    {formatAmount(account.credit_amount)}
                  </span>
                </div>

                <div className={cardRowClass}>
                  <span className={cardLabelClass}>જાવક</span>
                  <span className={debitCellClass}>
                    {formatAmount(account.debit_amount)}
                  </span>
                </div>

                <div className={cardRowClass}>
                  <span className={cardLabelClass}>બેલેન્સ</span>
                  <span className={moneyCellClass}>
                    {formatAmount(account.balance)}
                  </span>
                </div>

                <div className={cardRowClass}>
                  <span className={cardLabelClass}>તારીખ</span>
                  <span className="text-right text-gray-500">
                    {formatDate(account.date || account.created_at)}
                    <span className="block text-xs text-gray-400">
                      {formatDateTime(account.created_at)}
                    </span>
                  </span>
                </div>
              </div>

              {account.isEditable && actionCount > 0 && (
                <div
                  className={cx(
                    "grid gap-2 border-t p-4",
                    actionCount === 1 ? "grid-cols-1" : "grid-cols-2"
                  )}
                >
                  {isActionLoading === account.id ? (
                    <div className="col-span-full flex h-10 items-center justify-center">
                      <div className={actionSpinnerClass}></div>
                    </div>
                  ) : (
                    <>
                      {canUpdate && (
                        <Button
                          variant="outline"
                          title="સંપાદિત કરો"
                          onClick={() => handleEditAccount(account)}
                        >
                          <LuPencil className="h-4 w-4" />
                        </Button>
                      )}

                      {canDelete && (
                        <Button
                          variant="outline"
                          title="કાઢી નાખો"
                          className="text-red-600 hover:text-red-700"
                          onClick={() => handleDeleteRow(account.id)}
                        >
                          <LuTrash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {!isLoading && accounts.length > 0 && (
        <Pagination
          itemsPerPage={pageSize}
          totalItems={totalAccounts}
          paginate={paginate}
          currentPage={page}
          onPageSizeChange={handlePageSizeChange}
          itemLabel="એન્ટ્રી"
        />
      )}

      {isAccountModalOpen > 0 && (
        <AccountFormModal handleSaveAccount={handleSaveAccount} />
      )}
    </div>
  );
};
export default AccountList;
